import { apiService } from './ApiService';
import { AxiosResponse } from 'axios';

export interface AlgorithmSetting {
    key: string;
    type: 'bool' | 'int' | 'float';
    label: string;
    help: string;
    default: any;
    min: number | null;
    max: number | null;
    env_value: any;
    value: any;       // admin override, null when none
    effective: any;
    updated_by: string | null;
    updated_at: string | null;
}

export interface AdminStats {
    users: {
        total: number;
        admins: number;
        regular: number;
    };
    sessions: {
        total: number;
        active: number;
    };
}

export interface AdminUser {
    id: number;
    uid: string;
    name: string;
    middle_name?: string;
    last_name?: string;
    email: string;
    picture?: string;
    role: string;
    is_active: boolean;
    last_login?: number | null;
    session_count: number;
    organization_id?: number | null;
    organization_name?: string | null;
}

export interface AdminUsersPagedResponse {
    items: AdminUser[];
    total: number;
    limit: number;
    offset: number;
}

export interface AdminUserCreateRequest {
    name: string;
    middle_name?: string;
    last_name?: string;
    email: string;
    password: string;
    role: string;
    send_welcome_email: boolean;
    welcome_email_password?: string | null;
    welcome_email_locale?: string;
    organization_id?: number | null;
    organization_role?: string;
}

export interface UserSession {
    id: string;
    session_id: string;
    user_id: string;
    user_name?: string;
    user_email?: string;
    user_picture?: string;
    last_update_time: number;
    app_name: string;
    state?: Record<string, any>;
    state_keys?: string[];
}

export interface ContextData {
    id: number;
    user_uuid: string | null;
    context_data: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export interface ContextCreateRequest {
    user_uuid?: string;
    context_data: Record<string, any>;
}

export interface AgentSkill {
    skill_folder: string;
    file_name: string;
    file_path: string;
    content?: string;
    lines?: number;
    size_bytes?: number;
    last_modified?: number;
    error?: string;
}

export interface AgentSkillGroup {
    agent_folder: string;
    agent_name: string;
    skills: AgentSkill[];
}

export interface AgentSkillsListResponse {
    status: string;
    agents: AgentSkillGroup[];
}

export interface AgentInstruction {
    agent_name: string;
    folder: string;
    file_name: string;
    file_path: string;
    content?: string;
    lines?: number;
    size_bytes?: number;
    last_modified?: number;
    error?: string;
}

export interface AgentInstructionsListResponse {
    status: string;
    total_agents: number;
    agents: AgentInstruction[];
    timestamp?: number;
}

export interface SessionUser {
    uid: string;
    name: string;
    email: string;
    picture?: string;
}

export interface SessionDetails {
    id: string;
    session_id: string;
    user_id: string;
    app_name: string;
    created_at: string;
    updated_at: string;
    state: any;
}

export interface SessionEvent {
    id?: string;
    invocation_id: string;
    author: string;
    content?: {
        role: string;
        parts: Array<{
            text?: string;
            function_call?: any;
            function_response?: any;
        }>;
    };
    actions?: {
        state_delta?: any;
    };
    timestamp: string;
    // ADK 2.0: new graph-state tracking fields
    node_info?: any;
    output?: any;
}

export interface SessionDetailsResponse {
    status: string;
    user: SessionUser;
    session: SessionDetails;
    events: SessionEvent[];
}

export interface SharedCatalogAssignment {
    organization_id: number;
    organization_name: string;
    enabled: boolean;
    overrides?: Record<string, any> | null;
}

export interface SharedCatalogSessionStatus {
    status: 'success' | 'error';
    checked_at: number;      // epoch seconds of the last keepalive sweep
    expires_at?: number | null;
    message?: string;
}

export interface SharedCatalogConfig {
    id: number;
    slug: string;
    catalog_name: string;
    catalog_implementation: string;
    is_active: boolean;
    keepalive_enabled: boolean;
    created_by?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    assignments: SharedCatalogAssignment[];
    session?: SharedCatalogSessionStatus | null;
    email_otp?: Record<string, any> | null;
    brands?: string[] | null;
}

export interface BillingPlan {
    id: number;
    name: string;
    description: string | null;
    currency: string;
    monthly_price_cents: number | null;
    included_quotes_monthly: number | null;
    overage_price_cents: number | null;
    allowance_searches: number | null;
    allowance_images: number | null;
    allowance_pdfs: number | null;
    metric_limits: Record<string, number | null> | null;
    is_active: boolean;
    orgs_assigned: number;
    created_by?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface BillingPlanBody {
    name?: string;
    description?: string | null;
    currency?: string;
    monthly_price_cents?: number | null;
    included_quotes_monthly?: number | null;
    overage_price_cents?: number | null;
    allowance_searches?: number | null;
    allowance_images?: number | null;
    allowance_pdfs?: number | null;
    metric_limits?: Record<string, number> | null;
    is_active?: boolean;
    clear_fields?: string[];
}

export class AdminService {
    private static instance: AdminService;

    public static getInstance(): AdminService {
        if (!AdminService.instance) {
            AdminService.instance = new AdminService();
        }
        return AdminService.instance;
    }

    // Stats
    public async getStats(): Promise<AxiosResponse<AdminStats>> {
        return apiService.get('/api/admin/stats');
    }

    // Users
    public async getUsers(params?: { limit?: number; offset?: number; search?: string; role?: string; status?: string; organization_id?: number }): Promise<AxiosResponse<AdminUsersPagedResponse>> {
        return apiService.get('/api/admin/users', { params });
    }

    public async createUser(data: AdminUserCreateRequest): Promise<AxiosResponse<AdminUser>> {
        return apiService.post('/api/admin/users', data);
    }

    public async promoteUser(userUid: string): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/admin/users/${userUid}/promote`);
    }

    public async demoteUser(userUid: string): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/admin/users/${userUid}/demote`);
    }

    public async updateUserStatus(userUid: string, isActive: boolean): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/admin/users/${userUid}/status`, null, {
            params: { is_active: isActive }
        });
    }

    public async updateUser(userUid: string, data: { name?: string; email?: string; role?: string; organization_id?: number | null }): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/users/${userUid}`, data);
    }

    public async sendWelcomeEmail(userUid: string, password?: string, locale: string = 'en'): Promise<AxiosResponse<{ success: boolean; email: string }>> {
        return apiService.post(`/api/admin/users/${userUid}/welcome-email`, { password: password ?? null, locale });
    }

    // Sessions
    public async getSessions(limit: number = 10, offset: number = 0): Promise<AxiosResponse<{ sessions: UserSession[] }>> {
        return apiService.get(`/api/admin/sessions?limit=${limit}&offset=${offset}`);
    }

    public async getSessionDetails(userId: string, sessionId: string): Promise<AxiosResponse<SessionDetailsResponse>> {
        return apiService.get(`/api/admin/users/${userId}/sessions/${sessionId}`);
    }

    // Context
    public async getContextData(): Promise<AxiosResponse<{ contexts: ContextData[] }>> {
        return apiService.get('/api/admin/context');
    }

    public async createContext(data: { user_uuid?: string | null; context_data: Record<string, any> }): Promise<AxiosResponse<any>> {
        return apiService.post('/api/admin/context', data);
    }

    public async updateContext(contextId: number, data: any): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/context/${contextId}`, data);
    }

    public async deleteContext(contextId: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/context/${contextId}`);
    }

    // Agents
    public async getAgentInstructions(): Promise<AxiosResponse<{ agents: AgentInstruction[] }>> {
        return apiService.get('/api/admin/agents/instructions');
    }

    public async updateAgentInstruction(agentFolder: string, content: string): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/agents/instructions/${agentFolder}`, { content });
    }

    public async getAgentSkills(): Promise<AxiosResponse<AgentSkillsListResponse>> {
        return apiService.get('/api/admin/agents/skills');
    }

    public async updateAgentSkill(agentFolder: string, skillFolder: string, fileName: string, content: string): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/agents/skills/${agentFolder}/${skillFolder}/${fileName}`, { content });
    }

    // Shared catalog configs (credentials live in Secret Manager, never returned)
    public async getSharedCatalogs(): Promise<AxiosResponse<{ shared_catalogs: SharedCatalogConfig[] }>> {
        return apiService.get('/api/admin/shared-catalogs');
    }

    public async createSharedCatalog(data: {
        catalog_name: string;
        catalog_implementation: string;
        catalog_user: string;
        catalog_password: string;
        slug?: string;
        email_otp?: Record<string, any> | null;
        catalog_company?: string;
        brands?: string[] | null;
    }): Promise<AxiosResponse<SharedCatalogConfig>> {
        return apiService.post('/api/admin/shared-catalogs', data);
    }

    public async updateSharedCatalog(configId: number, data: {
        catalog_name?: string;
        catalog_implementation?: string;
        catalog_user?: string;
        catalog_password?: string;
        is_active?: boolean;
        keepalive_enabled?: boolean;
        email_otp?: Record<string, any> | null;
        catalog_company?: string;
        brands?: string[] | null;
    }): Promise<AxiosResponse<SharedCatalogConfig>> {
        return apiService.put(`/api/admin/shared-catalogs/${configId}`, data);
    }

    public async deleteSharedCatalog(configId: number, hard: boolean = false): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/shared-catalogs/${configId}`, { params: { hard } });
    }

    public async testSharedCatalog(configId: number): Promise<AxiosResponse<any>> {
        // Vendor logins (Secret Manager read + catalog auth, microcat device
        // registration retries) can exceed the 30s axios default.
        // A configured e-mail OTP cycle may legitimately take several minutes.
        return apiService.post(`/api/admin/shared-catalogs/${configId}/test`, undefined, { timeout: 0 });
    }

    public async assignSharedCatalog(configId: number, organizationId: number, overrides?: Record<string, any>): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/admin/shared-catalogs/${configId}/assignments`, {
            organization_id: organizationId,
            overrides: overrides || null,
            enabled: true,
        });
    }

    public async unassignSharedCatalog(configId: number, organizationId: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/shared-catalogs/${configId}/assignments/${organizationId}`);
    }

    public async getSessionKeeperState(): Promise<AxiosResponse<{ enabled: boolean; env_gate: boolean; interval_seconds: number }>> {
        return apiService.get('/api/admin/shared-catalogs/keeper');
    }

    public async setSessionKeeperEnabled(enabled: boolean): Promise<AxiosResponse<{ enabled: boolean }>> {
        return apiService.put('/api/admin/shared-catalogs/keeper', { enabled });
    }

    // Organizations
    public async getOrganizations(): Promise<AxiosResponse<{ organizations: any[] }>> {
        return apiService.get('/api/admin/organizations');
    }

    /** Switch the calling admin (e.g. Cata Support) into an organization; null switches out. */
    public async setWorkingOrganization(
        organizationId: number | null,
    ): Promise<AxiosResponse<{ organization_id: number | null; organization_name: string | null }>> {
        return apiService.put('/api/admin/me/organization', { organization_id: organizationId });
    }

    public async createOrganization(name: string, description: string): Promise<AxiosResponse<any>> {
        return apiService.post('/api/admin/organizations', null, {
            params: { name, description }
        });
    }

    public async updateOrganization(orgId: number, updates: any): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/organizations/${orgId}`, null, { params: updates });
    }

    public async deleteOrganization(orgId: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/organizations/${orgId}`);
    }

    public async getOrganizationMembers(orgId: number): Promise<AxiosResponse<{ members: any[] }>> {
        return apiService.get(`/api/admin/organizations/${orgId}/members`);
    }

    public async addOrganizationMember(orgId: number, userEmail: string, role: string): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/admin/organizations/${orgId}/members`, null, {
            params: { user_email: userEmail, role }
        });
    }

    public async removeOrganizationMember(orgId: number, userUid: string): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/organizations/${orgId}/members/${userUid}`);
    }

    public async updateMemberRole(orgId: number, userUid: string, role: string): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/organizations/${orgId}/members/${userUid}`, null, {
            params: { role }
        });
    }

    public async getOrganizationSettings(orgId: number): Promise<AxiosResponse<any>> {
        return apiService.get(`/api/admin/organizations/${orgId}/settings`);
    }

    public async updateOrganizationSettings(orgId: number, settings: { language?: string; currency?: string; country?: string; mission_critical?: boolean; external_enrichment?: boolean; daily_api_call_limit?: number | null; plan_id?: string; plan_limit_overrides?: string; usage_enforcement?: string; billing_plan_id?: number; quote_monthly_price_cents?: number; quote_included_monthly?: number; quote_overage_price_cents?: number; quote_allowance_searches?: number; quote_allowance_images?: number; quote_allowance_pdfs?: number; roi_calculator_enabled?: boolean; roi_b2b_enabled?: boolean; roi_b2c_enabled?: boolean; roi_monthly_quotes?: number | null; roi_fte_multiplier?: number | null; roi_currency?: string | null; roi_fte_cost?: number | null; roi_cost_per_quote?: number | null; roi_b2c_quotes?: number | null; roi_b2c_cr?: number | null; roi_b2c_aov?: number | null; roi_b2c_gm?: number | null; roi_b2c_cpq?: number | null; roi_b2c_pct?: number | null; roi_b2c_pricing_mode?: string | null }): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/organizations/${orgId}/settings`, null, {
            params: settings
        });
    }

    // Billing plans
    public async listBillingPlans(): Promise<AxiosResponse<{ plans: BillingPlan[] }>> {
        return apiService.get('/api/admin/billing-plans');
    }

    public async createBillingPlan(body: BillingPlanBody): Promise<AxiosResponse<BillingPlan>> {
        return apiService.post('/api/admin/billing-plans', body);
    }

    public async updateBillingPlan(planId: number, body: BillingPlanBody): Promise<AxiosResponse<BillingPlan>> {
        return apiService.put(`/api/admin/billing-plans/${planId}`, body);
    }

    public async deleteBillingPlan(planId: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/billing-plans/${planId}`);
    }

    // System Health
    public async getSystemHealth(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/admin/system/health');
    }

    // Audit Log
    public async getAuditLog(limit: number = 50, offset: number = 0, actionType?: string): Promise<AxiosResponse<any>> {
        let url = `/api/admin/audit-log?limit=${limit}&offset=${offset}`;
        if (actionType) url += `&action_type=${actionType}`;
        return apiService.get(url);
    }

    // Feedback
    public async getFeedbackSummary(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/admin/feedback/summary');
    }

    public async getRecentFeedback(limit: number = 50, offset: number = 0, feedbackType?: string): Promise<AxiosResponse<any>> {
        let url = `/api/admin/feedback/recent?limit=${limit}&offset=${offset}`;
        if (feedbackType) url += `&feedback_type=${feedbackType}`;
        return apiService.get(url);
    }

    // Orders
    public async getAllOrders(limit: number = 50, offset: number = 0, status?: string): Promise<AxiosResponse<any>> {
        let url = `/api/admin/orders?limit=${limit}&offset=${offset}`;
        if (status) url += `&status=${status}`;
        return apiService.get(url);
    }

    public async getOrderDetails(orderId: number): Promise<AxiosResponse<any>> {
        return apiService.get(`/api/admin/orders/${orderId}`);
    }

    public async updateOrderStatus(orderId: number, newStatus: string): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/orders/${orderId}/status`, null, {
            params: { new_status: newStatus }
        });
    }

    // Assistance Requests
    public async getAssistanceRequests(limit: number = 50, offset: number = 0, status?: string, priority?: string): Promise<AxiosResponse<any>> {
        let url = `/api/admin/assistance-requests?limit=${limit}&offset=${offset}`;
        if (status) url += `&status=${status}`;
        if (priority) url += `&priority=${priority}`;
        return apiService.get(url);
    }

    public async updateAssistanceRequestStatus(requestId: number, newStatus: string, resolutionNotes?: string): Promise<AxiosResponse<any>> {
        const params: any = { new_status: newStatus };
        if (resolutionNotes) params.resolution_notes = resolutionNotes;
        return apiService.put(`/api/admin/assistance-requests/${requestId}/status`, null, { params });
    }

    // Feature Flags
    public async getFeatureFlags(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/admin/feature-flags');
    }

    public async updateFeatureFlag(flagName: string, isEnabled: boolean): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/feature-flags/${flagName}`, null, {
            params: { is_enabled: isEnabled }
        });
    }

    // Org-scoped Feature Flags
    public async getOrgFeatureFlags(orgId: number): Promise<AxiosResponse<any>> {
        return apiService.get(`/api/admin/organizations/${orgId}/feature-flags`);
    }

    public async updateOrgFeatureFlag(orgId: number, flagName: string, isEnabled: boolean): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/organizations/${orgId}/feature-flags/${flagName}`, null, {
            params: { is_enabled: isEnabled }
        });
    }

    // Org licence plate → VIN lookup (bl/plate_lookup)
    public async getOrgPlateLookup(orgId: number): Promise<AxiosResponse<any>> {
        return apiService.get(`/api/admin/organizations/${orgId}/plate-lookup`);
    }

    public async updateOrgPlateLookup(orgId: number, config: { enabled: boolean; country: string; mcp_url: string; token?: string }): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/organizations/${orgId}/plate-lookup`, config);
    }

    public async testOrgPlateLookup(orgId: number, plate: string): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/admin/organizations/${orgId}/plate-lookup/test`, { plate });
    }

    public async getOrgFeatureFlagPublic(orgId: number, flagName: string): Promise<AxiosResponse<any>> {
        return apiService.get(`/api/admin/public/organizations/${orgId}/feature-flags/${flagName}`);
    }

    /** Global (platform-wide) flag — no org override. Used by flags that are
     *  global by design, e.g. external_enrichment. */
    public async getGlobalFeatureFlagPublic(flagName: string): Promise<AxiosResponse<any>> {
        return apiService.get(`/api/admin/public/feature-flags/${flagName}`);
    }

    // User Search
    public async searchUsers(params: {
        q?: string;
        role?: string;
        is_active?: boolean;
        organization_id?: number;
        sort_by?: string;
        sort_order?: string;
        limit?: number;
        offset?: number;
    }): Promise<AxiosResponse<any>> {
        const searchParams = new URLSearchParams();
        if (params.q) searchParams.append('q', params.q);
        if (params.role) searchParams.append('role', params.role);
        if (params.is_active !== undefined) searchParams.append('is_active', String(params.is_active));
        if (params.organization_id !== undefined) searchParams.append('organization_id', String(params.organization_id));
        if (params.sort_by) searchParams.append('sort_by', params.sort_by);
        if (params.sort_order) searchParams.append('sort_order', params.sort_order);
        if (params.limit) searchParams.append('limit', String(params.limit));
        if (params.offset) searchParams.append('offset', String(params.offset));
        return apiService.get(`/api/admin/users/search?${searchParams.toString()}`);
    }

    // Bulk User Actions
    public async bulkUpdateUserStatus(userUids: string[], isActive: boolean): Promise<AxiosResponse<any>> {
        return apiService.post('/api/admin/users/bulk/status', {
            user_uids: userUids,
            is_active: isActive
        });
    }

    // Data Export
    public async exportUsersCsv(): Promise<AxiosResponse<Blob>> {
        return apiService.get('/api/admin/export/users', { responseType: 'blob' });
    }

    public async exportOrdersCsv(status?: string): Promise<AxiosResponse<Blob>> {
        let url = '/api/admin/export/orders';
        if (status) url += `?status=${status}`;
        return apiService.get(url, { responseType: 'blob' });
    }

    public async exportFeedbackCsv(): Promise<AxiosResponse<Blob>> {
        return apiService.get('/api/admin/export/feedback', { responseType: 'blob' });
    }

    public async exportAuditLogCsv(): Promise<AxiosResponse<Blob>> {
        return apiService.get('/api/admin/export/audit-log', { responseType: 'blob' });
    }

    // Detailed Stats
    public async getDetailedStats(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/admin/stats/detailed');
    }

    // System Messages
    public async getSystemMessages(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/admin/system-messages');
    }

    public async createSystemMessage(data: {
        title: string;
        message: string;
        message_type: string;
        is_active: boolean;
        organization_id?: number | null;
    }): Promise<AxiosResponse<any>> {
        return apiService.post('/api/admin/system-messages', data);
    }

    public async updateSystemMessage(id: number, data: {
        title?: string;
        message?: string;
        message_type?: string;
        is_active?: boolean;
        organization_id?: number | null;
    }): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/admin/system-messages/${id}`, data);
    }

    public async deleteSystemMessage(id: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/admin/system-messages/${id}`);
    }

    public async getActiveSystemMessages(orgId?: number): Promise<AxiosResponse<any>> {
        const params = orgId ? `?org_id=${orgId}` : '';
        return apiService.get(`/api/admin/public/system-messages${params}`);
    }

    // Usage Dashboard
    public async getUsageDashboard(): Promise<AxiosResponse<any>> {
        return apiService.get('/api/admin/usage');
    }

    // Parts Operations Dashboard
    public async getOrgDashboard(params: {
        org_id: number;
        start_date?: string;
        end_date?: string;
        app_name?: string;
        assumed_price?: number;
    }): Promise<AxiosResponse<any>> {
        const p = new URLSearchParams({ org_id: String(params.org_id) });
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (params.app_name) p.append('app_name', params.app_name);
        if (params.assumed_price !== undefined) p.append('assumed_price', String(params.assumed_price));
        return apiService.get(`/api/admin/org-dashboard?${p.toString()}`);
    }

    // Search-algorithm settings (admin panel, System tab)
    public async getAlgorithmSettings(): Promise<AxiosResponse<{ status: string; settings: AlgorithmSetting[] }>> {
        return apiService.get('/api/admin/algorithm-settings');
    }

    public async updateAlgorithmSetting(key: string, value: string | null): Promise<AxiosResponse<{ status: string; key: string; effective: any }>> {
        return apiService.put(`/api/admin/algorithm-settings/${key}`, { value });
    }

    // Cloud Run jobs
    public async runCloudRunJob(jobKey: string): Promise<AxiosResponse<{ status: string; message: string; operation?: string }>> {
        return apiService.post(`/api/admin/jobs/${jobKey}/run`);
    }

    // ── Redis browser ────────────────────────────────────────────────────────
    public async scanRedisKeys(params: { pattern?: string; cursor?: number; count?: number }): Promise<AxiosResponse<RedisScanResponse>> {
        return apiService.get('/api/admin/redis/keys', {
            params: {
                pattern: params.pattern || '*',
                cursor: params.cursor ?? 0,
                count: params.count ?? 100,
            },
        });
    }

    public async getRedisStats(sampleLimit: number = 1000): Promise<AxiosResponse<RedisStatsResponse>> {
        return apiService.get('/api/admin/redis/stats', { params: { sample_limit: sampleLimit } });
    }

    public async getRedisValue(key: string): Promise<AxiosResponse<RedisValueResponse>> {
        return apiService.get('/api/admin/redis/value', { params: { key } });
    }

    public async setRedisValue(data: { key: string; value: string; ttl?: number | null }): Promise<AxiosResponse<any>> {
        return apiService.put('/api/admin/redis/value', data);
    }

    public async deleteRedisKey(key: string): Promise<AxiosResponse<any>> {
        return apiService.delete('/api/admin/redis/key', { params: { key } });
    }

    // ── WMI manufacturers ────────────────────────────────────────────────────
    public async listWmi(params: { q?: string; source?: string; known?: boolean; limit?: number; offset?: number }): Promise<AxiosResponse<{ total: number; rows: WmiRow[] }>> {
        return apiService.get('/api/admin/wmi', { params });
    }

    public async exportWmiCsv(params: { q?: string; source?: string; known?: boolean }): Promise<AxiosResponse<Blob>> {
        // `_` busts any browser-cached response for this URL (an earlier HTML fallback was replayed).
        return apiService.get('/api/admin/wmi/export', { params: { ...params, _: Date.now() }, responseType: 'blob' });
    }

    public async updateWmi(wmi: string, data: { symbol: string; manufacturer: string | null }): Promise<AxiosResponse<{ status: string; row: WmiRow }>> {
        return apiService.put(`/api/admin/wmi/${encodeURIComponent(wmi)}`, data);
    }

    // ---- Pick memory (autopilot): what a line resolved to per org + vehicle family,
    // and the component readings learned to name the same part (routes/admin.py "PICK MEMORY").
    public async getMemorySummary(): Promise<AxiosResponse<{ status: string; autopilot_enabled: boolean; org_memory_applied?: boolean; org_memory_recorded?: boolean; picks: any[]; pairs: any[] }>> {
        return apiService.get('/api/admin/memory/summary');
    }

    public async listPickMemory(params: { org_id?: string; catalog?: string; q?: string; source?: string; enabled?: boolean; profile_id?: number; limit?: number; offset?: number }): Promise<AxiosResponse<{ total: number; rows: PickMemoryRow[] }>> {
        return apiService.get('/api/admin/memory/picks', { params });
    }

    public async createPickMemory(data: { organization_id: number | null; catalog: string; vin: string; line: string; part_id: string; part_name?: string | null }): Promise<AxiosResponse<{ status: string }>> {
        return apiService.post('/api/admin/memory/picks', data);
    }

    public async updatePickMemory(id: number, data: { part_id?: string; part_name?: string | null; enabled?: boolean; shared?: boolean }): Promise<AxiosResponse<{ status: string; row: PickMemoryRow }>> {
        return apiService.put(`/api/admin/memory/picks/${id}`, data);
    }

    public async deletePickMemory(id: number): Promise<AxiosResponse<{ status: string }>> {
        return apiService.delete(`/api/admin/memory/picks/${id}`);
    }

    public async deletePickMemoryBulk(params: { org_id?: string; catalog?: string; q?: string; source?: string; enabled?: boolean; confirm: number }): Promise<AxiosResponse<{ status: string; deleted: number }>> {
        return apiService.delete('/api/admin/memory/picks', { params });
    }

    public async deleteComponentPairsBulk(params: { org_id?: string; catalog?: string; q?: string; enabled?: boolean; confirm: number }): Promise<AxiosResponse<{ status: string; deleted: number }>> {
        return apiService.delete('/api/admin/memory/pairs', { params });
    }

    public async importPickMemory(file: File, params: { catalog: string; org_id?: string; reset_seed?: boolean }): Promise<AxiosResponse<{ status: string; written: number; skipped: string[]; seed_rows_removed: number }>> {
        const fd = new FormData();
        fd.append('file', file);
        return apiService.post('/api/admin/memory/picks/import', fd, {
            params, headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    public async exportPickMemory(params: { org_id?: string; catalog?: string }): Promise<AxiosResponse<Blob>> {
        return apiService.get('/api/admin/memory/picks/export', { params: { ...params, _: Date.now() }, responseType: 'blob' });
    }

    public async listComponentPairs(params: { org_id?: string; catalog?: string; q?: string; enabled?: boolean; profile_id?: number; limit?: number; offset?: number }): Promise<AxiosResponse<{ total: number; rows: ComponentPairRow[] }>> {
        return apiService.get('/api/admin/memory/pairs', { params });
    }

    public async createComponentPair(data: { organization_id: number | null; catalog: string; a: string; b: string }): Promise<AxiosResponse<{ status: string }>> {
        return apiService.post('/api/admin/memory/pairs', data);
    }

    public async updateComponentPair(id: number, data: { enabled?: boolean; shared?: boolean }): Promise<AxiosResponse<{ status: string; row: ComponentPairRow }>> {
        return apiService.put(`/api/admin/memory/pairs/${id}`, data);
    }

    public async deleteComponentPair(id: number): Promise<AxiosResponse<{ status: string }>> {
        return apiService.delete(`/api/admin/memory/pairs/${id}`);
    }

    // ── Selection profiles (learned selection behaviour) ──
    public async listTrainingSets(catalog?: string): Promise<AxiosResponse<{ rows: TrainingSetRow[] }>> {
        return apiService.get('/api/admin/profiles/training-sets', { params: catalog ? { catalog } : {} });
    }

    public async importTrainingSet(file: File, params: { name?: string; catalog: string; org_id?: number; replay?: boolean; language?: string }): Promise<AxiosResponse<{ status: string; row: TrainingSetRow; imported: number }>> {
        const fd = new FormData();
        fd.append('file', file);
        return apiService.post('/api/admin/profiles/training-sets/import', fd, {
            params, headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    public async getTrainingSet(id: number, params?: { limit?: number; offset?: number }): Promise<AxiosResponse<{ row: TrainingSetRow; total: number; examples: TrainingExampleRow[] }>> {
        return apiService.get(`/api/admin/profiles/training-sets/${id}`, { params });
    }

    public async replayTrainingSet(id: number, allRows = false): Promise<AxiosResponse<{ status: string; started: boolean }>> {
        return apiService.post(`/api/admin/profiles/training-sets/${id}/replay`, null, { params: { all_rows: allRows } });
    }

    public async deleteTrainingSet(id: number): Promise<AxiosResponse<{ status: string }>> {
        return apiService.delete(`/api/admin/profiles/training-sets/${id}`);
    }

    public async listProfiles(catalog?: string): Promise<AxiosResponse<{ rows: SelectionProfileRow[] }>> {
        return apiService.get('/api/admin/profiles', { params: catalog ? { catalog } : {} });
    }

    public async createProfile(data: { name: string; catalog: string; description?: string | null; training_set_ids: number[]; settings?: Record<string, number> | null }): Promise<AxiosResponse<{ status: string; row: SelectionProfileRow }>> {
        return apiService.post('/api/admin/profiles', data);
    }

    public async updateProfile(id: number, data: { name: string; catalog: string; description?: string | null; training_set_ids: number[]; settings?: Record<string, number> | null; enabled?: boolean }): Promise<AxiosResponse<{ status: string; row: SelectionProfileRow }>> {
        return apiService.put(`/api/admin/profiles/${id}`, data);
    }

    public async mineProfile(id: number): Promise<AxiosResponse<{ status: string; summary: MineSummary }>> {
        return apiService.post(`/api/admin/profiles/${id}/mine`);
    }

    public async deleteProfile(id: number): Promise<AxiosResponse<{ status: string }>> {
        return apiService.delete(`/api/admin/profiles/${id}`);
    }

    public async listProfileRules(id: number): Promise<AxiosResponse<{ rows: SelectionRuleRow[] }>> {
        return apiService.get(`/api/admin/profiles/${id}/rules`);
    }

    public async updateProfileRule(ruleId: number, data: { enabled?: boolean; status?: 'active' | 'proposed' }): Promise<AxiosResponse<{ status: string; row: SelectionRuleRow }>> {
        return apiService.put(`/api/admin/profile-rules/${ruleId}`, data);
    }

    public async deleteProfileRule(ruleId: number): Promise<AxiosResponse<{ status: string }>> {
        return apiService.delete(`/api/admin/profile-rules/${ruleId}`);
    }

    public async listProfileAssignments(): Promise<AxiosResponse<{ rows: ProfileAssignmentRow[] }>> {
        return apiService.get('/api/admin/profile-assignments');
    }

    public async setProfileAssignment(data: { organization_id: number | null; catalog: string; profile_id: number; source?: string; priority?: number }): Promise<AxiosResponse<{ status: string; row: ProfileAssignmentRow }>> {
        return apiService.put('/api/admin/profile-assignments', data);
    }

    public async deleteProfileAssignment(id: number): Promise<AxiosResponse<{ status: string }>> {
        return apiService.delete(`/api/admin/profile-assignments/${id}`);
    }

    public async uploadWmiCsv(file: File): Promise<AxiosResponse<{ status: string; upserted: number }>> {
        const fd = new FormData();
        fd.append('file', file);
        return apiService.post('/api/admin/wmi/upload', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
}

export interface TrainingSetRow {
    id: number;
    name: string;
    catalog: string;
    organization_id: number | null;
    source: string;
    status: string;          // imported | replaying | ready | ready_with_errors | failed
    rows_total: number;
    rows_replayed: number;
    notes: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface TrainingExampleRow {
    id: number;
    ref: string | null;
    vin: string;
    line: string;
    chosen: string[];
    request: { component?: string; axes?: Record<string, string> } | null;
    candidates: number | null;
    source: string;
    replayed_at: string | null;
}

export interface SelectionProfileRow {
    id: number;
    name: string;
    catalog: string;
    description: string | null;
    training_set_ids: number[];
    settings: Record<string, number> | null;
    enabled: boolean;
    mined_at: string | null;
    rules: number | null;
    organizations: number[];
    created_at: string | null;
}

export interface SelectionRuleRow {
    id: number;
    profile_id: number;
    kind: string;
    family_key: string;
    family_label: string | null;
    feature: string;
    direction: boolean;
    support: number;
    agreement: number;
    examples: string[];
    status: string;
    enabled: boolean;
    text: string;
    updated_at: string | null;
}

export interface ProfileAssignmentRow {
    id: number;
    organization_id: number | null;   // null = every organisation
    catalog: string;
    source: string;        // '' = any order source
    profile_id: number;
    priority: number;
}

export interface MineSummary {
    profile_id: number;
    examples: number;
    rules: number;
    rules_dropped: number;
    inconsistent: number;
    equivalences: number;
    picks: number;
    inconsistent_families: Array<{ family_label: string; feature: string; support: number; agreement: number }>;
}

export interface PickMemoryRow {
    id: number;
    organization_id: number | null;
    catalog: string;
    vehicle_key: string;
    model_year: string | null;
    line_key: string;
    term_key: string | null;
    part_id: string;
    part_name: string | null;
    source: 'seed' | 'confirmed' | 'autopilot' | string;
    enabled: boolean;
    hits: number;
    last_seen: string | null;
    created_at: string | null;
}

export interface ComponentPairRow {
    id: number;
    organization_id: number | null;
    catalog: string;
    a: string;
    b: string;
    source: string;
    enabled: boolean;
    profile_id?: number | null;
    hits: number;
    last_seen: string | null;
    created_at: string | null;
}

export interface WmiRow {
    wmi: string;
    symbol: string;
    manufacturer: string | null;
    source: string;
    updated_at: string;
}

export interface RedisKeyInfo {
    key: string;
    type: string;
    ttl: number; // -1 = no expiry, -2 = missing
}

export interface RedisStatsResponse {
    status: string;
    dbsize: number;
    memory: {
        used_bytes: number;
        peak_bytes: number;
        max_bytes: number;
        fragmentation_ratio: number;
    };
    traffic: {
        operations_per_second: number;
        total_commands: number;
        evicted_keys: number;
        rejected_connections: number;
        hit_rate_percent: number | null;
    };
    clients: {
        connected: number;
        blocked: number;
        max_input_buffer_bytes: number;
        max_output_buffer_bytes: number;
    };
    prefixes: Array<{
        prefix: string;
        keys: number;
        bytes: number;
        no_expiry: number;
    }>;
    sampled_keys: number;
    sample_complete: boolean;
    sample_limit: number;
    top_commands: Array<{
        command: string;
        calls: number;
        usec_per_call: number;
    }>;
}

export interface RedisScanResponse {
    status: string;
    pattern: string;
    cursor: number;
    has_more: boolean;
    keys: RedisKeyInfo[];
    dbsize: number | null;
}

export interface RedisValueResponse {
    status: string;
    key: string;
    type: string;
    ttl: number;
    editable: boolean;
    value: any;
    encoding: 'utf-8' | 'base64' | 'collection' | 'none' | 'unsupported';
    truncated: boolean;
    size_bytes?: number;
}

export const adminService = AdminService.getInstance();
